import { setUserId as amplitudeSetUserId, track } from '@amplitude/analytics-browser';
import { UserPermissionStatus, UserSource } from 'common-types';
import type { TAuthSearchParams } from 'router/schemas';
import { api } from 'services/api';
import { useLocalStorageStore, useStore } from 'store';

type TInitSessionParams = {
  searchParams: TAuthSearchParams;
  pathname: string;
};

type TInitSessionResult = {
  isUserLoggedIn: boolean;
  userId: string | null;
};

/**
 * Tracks login events using Amplitude directly (no React hooks)
 */
const trackLoginEvent = (
  event: string,
  extraFields: Record<string, string | number | boolean>,
  userId?: string,
): void => {
  const options = userId ? { user_id: userId } : undefined;
  track(event, { user_id: userId, ...extraFields }, options);
};

/**
 * Standalone session initialization function for use in TanStack Router beforeLoad.
 * Does NOT use React hooks - accesses Zustand stores directly via getState().
 */
export const initSession = async (params: TInitSessionParams): Promise<TInitSessionResult> => {
  const { searchParams } = params;

  // Access stores directly (not as hooks)
  const localStorageState = useLocalStorageStore.getState();
  const storeState = useStore.getState();

  // If session is already initialized, return early
  if (storeState.isSessionInitialized) {
    return {
      isUserLoggedIn: localStorageState.userPermission === UserPermissionStatus.LOGGED_IN,
      userId: localStorageState.userId,
    };
  }

  // Extract params from URL
  const query = new URLSearchParams(window.location.search);
  const restoredOrderId = query.get('orderId');
  const restoredOrderIdFromLocation = searchParams.orderid;
  const restoredDevice = searchParams.device;
  const restoredStyleSession = searchParams.style;
  const openPaymentWindow = searchParams.openpayment;

  // Handle style session and payment modal params
  if (restoredStyleSession && openPaymentWindow) {
    storeState.setRestoredStyleSession(restoredStyleSession);
    storeState.setIsOpenPaymentModal(true);
  } else if (restoredStyleSession) {
    storeState.setRestoredStyleSession(restoredStyleSession);
  }

  // Try to restore session from URL orderId
  const orderIdToRestore = restoredOrderId || restoredOrderIdFromLocation;
  if (orderIdToRestore) {
    try {
      const { token, userServiceToken } = await api.restoreSession(orderIdToRestore);

      // Update localStorage store
      localStorageState.setUserId(orderIdToRestore);
      localStorageState.setToken(token);
      localStorageState.setUserServiceToken(userServiceToken);
      localStorageState.setIsUserLoggedIn(true);
      localStorageState.setUserPermission(UserPermissionStatus.LOGGED_IN);

      // Update session store
      storeState.setIsSessionInitialized(true);

      // Set Amplitude user
      amplitudeSetUserId(orderIdToRestore);

      // Handle iOS device source
      if (restoredDevice) {
        storeState.setSubscriptionUserSource(UserSource.IOS);
      }

      // Track successful login
      trackLoginEvent(
        'log_in_success',
        {
          auto_login: true,
          user_id: orderIdToRestore,
        },
        orderIdToRestore,
      );

      return { isUserLoggedIn: true, userId: orderIdToRestore };
    } catch (error) {
      trackLoginEvent('log_in_error', {
        error_message: JSON.stringify(error),
      });
    }
  }

  // Try to restore session from stored userId
  const existingUserId = localStorageState.userId;
  if (existingUserId) {
    try {
      const { token, userServiceToken } = await api.restoreSession(existingUserId);

      localStorageState.setToken(token);
      localStorageState.setUserServiceToken(userServiceToken);
      localStorageState.setIsUserLoggedIn(true);
      localStorageState.setUserPermission(UserPermissionStatus.LOGGED_IN);

      storeState.setIsSessionInitialized(true);

      trackLoginEvent(
        'log_in_success',
        {
          auto_login: true,
          user_id: existingUserId,
        },
        existingUserId,
      );

      return { isUserLoggedIn: true, userId: existingUserId };
    } catch (error) {
      trackLoginEvent('log_in_error', {
        error_message: JSON.stringify(error),
      });
    }
  }

  // No valid session found - mark as initialized but not logged in
  storeState.setIsSessionInitialized(true);
  return { isUserLoggedIn: false, userId: null };
};
